import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BellIcon, ChevronDownIcon, DramaIcon, LogOutIcon, PlayIcon, SearchIcon, SettingsIcon } from 'lucide-react';
import { useSidebarIdentity } from '@components/sidebar/sidebar-v2';
import { useLanguage } from '@contexts/LanguageContext';
import { useTextScale } from '@contexts/TextScaleContext';
import { Button } from '@/components/lumen/button';
import { SidebarNavGroup, SidebarNavItem, SidebarNavList } from '@/components/shell/sidebar/SidebarNav';
import { SidebarRail } from '@/components/shell/sidebar/SidebarRail';
import { BADGE_KEYS, useNavTree } from '@/components/shell/sidebar/use-nav-tree';
import { cn } from '@/lib/utils';
import { resolveFirstLoginCopy } from './i18n';
import type { FilmVariant } from './types';

/** The rail's own width at 100% text scale (AppFrame); scaled like the live rail. */
const RAIL_WIDTH = 240;
const FRAME_HEIGHT = 480;
/** How far the camera pushes in. */
const ZOOM = 1.25;
/** Room left above the Master row when the rail's nav is scrolled to it. */
const NAV_TOP_GAP = 8;

const MASTER_LABEL = 'Master';
const STATEMENT_LINK = '/master/statement';
const CONSIGNEE_LINK = '/master/consignee';

type Phase = 'rest' | 'zoom' | 'open' | 'statement' | 'consignee' | 'account' | 'pengaturan' | 'done';

/** Timelines, ms from play. */
const TIMELINES: Record<FilmVariant, Array<[number, Phase]>> = {
  // Master, its two pages, then Pengaturan in the top bar's account menu.
  full: [
    [0, 'rest'],
    [700, 'zoom'],
    [1700, 'open'],
    [2900, 'statement'],
    [4100, 'consignee'],
    [5400, 'account'],
    [6400, 'pengaturan'],
    [8400, 'done'],
  ],
  // No Master to show: straight to the account menu.
  pengaturan: [
    [0, 'rest'],
    [900, 'account'],
    [1900, 'pengaturan'],
    [4200, 'done'],
  ],
};

const MASTER_PHASES: Phase[] = ['zoom', 'open', 'statement', 'consignee'];
const MENU_PHASES: Phase[] = ['account', 'pengaturan'];
const OPEN_PHASES: Phase[] = ['open', 'statement', 'consignee'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Film-only hooks, never `data-tour`: a running tour must find the real rail's anchors, not these. */
const accountSelector = '[data-film-account]';
const pengaturanSelector = '[data-film-pengaturan]';

interface Point {
  x: number;
  y: number;
}

/** Scale ZOOM about a point of the stage (the Master row), with the transform origin at the stage's top-left. */
type Camera = { focus: Point } | null;

/**
 * Layout position of an element's top-left inside the stage, from the offset
 * chain: unaffected by the camera transform and by any scrolling ancestor.
 */
const layoutOffset = (el: HTMLElement, stage: HTMLElement): Point => {
  let x = 0;
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== stage) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return { x, y };
};

/** The display name the top bar's account menu shows, from the same `authData` it reads. */
const readAccountName = (): string => {
  try {
    const parsed = JSON.parse(localStorage.getItem('authData') || 'null');
    return typeof parsed?.name === 'string' ? parsed.name : '';
  } catch {
    return '';
  }
};

export interface SidebarFilmProps {
  variant?: FilmVariant;
  /** Line under the frame; the step picks it to match the variant and situation. */
  caption: string;
}

/**
 * The step-3 film: a small copy of the app frame, scripted. The rail is the
 * shell's own rail (SidebarRail + SidebarNavList + SidebarNavGroup /
 * SidebarNavItem over useNavTree, so the same tree, filters and locks the
 * user's sidebar shows), with no tour anchors. The camera zooms toward Master
 * while the rail scrolls to it, Master opens, Pernyataan and Data Consignee
 * take their real active state, then the camera pulls back to the whole frame,
 * the cursor goes to the top bar's account button, the menu opens and
 * Pengaturan is highlighted there (it moved from the old
 * rail footer to that menu). Every position on screen is measured from the
 * rendered nodes; nothing is hand-placed. The whole stage is inert and hidden
 * from assistive tech; the caption says what it shows.
 */
export default function SidebarFilm({ variant = 'full', caption }: SidebarFilmProps) {
  const { t: tApp, language } = useLanguage();
  const { scale: textScale } = useTextScale();
  const t = (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars);
  const { items, isGatewayUser } = useNavTree();
  const { userEmail, canImpersonate, isImpersonating } = useSidebarIdentity();
  const accountName = useMemo(readAccountName, []);

  const frameRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('rest');
  const [cursor, setCursor] = useState<Point | null>(null);
  const [camera, setCamera] = useState<Camera>(null);
  // Bumped on every play. A reduced-motion replay goes rest -> pengaturan in
  // one batch, so `phase` alone does not change and the effect that scrolls the
  // rail would not re-run.
  const [playCount, setPlayCount] = useState(0);
  const timersRef = useRef<number[]>([]);
  const reduced = useMemo(() => prefersReducedMotion(), []);

  const fullFilm = variant === 'full';
  const masterIndex = items.findIndex((item) => item.label === MASTER_LABEL);

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) {
      window.clearTimeout(id);
    }
    timersRef.current = [];
  }, []);

  const schedule = useCallback((delay: number, fn: () => void) => {
    timersRef.current.push(window.setTimeout(fn, delay));
  }, []);

  const railNav = useCallback(
    () => stageRef.current?.querySelector<HTMLElement>('[data-slot="sidebar-rail-nav"]') ?? null,
    []
  );

  /** Master's list item in the rail: the nav list renders one <li> per entry, in order. */
  const masterItem = useCallback((): HTMLElement | null => {
    const list = railNav()?.querySelector('ul');
    const item = masterIndex >= 0 ? list?.children[masterIndex] : null;
    return item instanceof HTMLElement ? item : null;
  }, [railNav, masterIndex]);

  /** Where the cursor should sit for an element, relative to the frame (camera included). */
  const measure = useCallback((el: Element | null | undefined): Point | null => {
    const frame = frameRef.current;
    if (!frame || !el) return null;
    const frameRect = frame.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    // Aim at the label, a third of the way in, rather than the exact centre.
    return { x: rect.left - frameRect.left + rect.width * 0.36, y: rect.top - frameRect.top + rect.height / 2 };
  }, []);

  /**
   * Scrolls the rail's nav so the Master row sits near its top (as far as the
   * nav can scroll) and returns that row's centre in stage coordinates at the
   * scroll it will settle on. Run again once Master is open: the open sub-list
   * is what gives a last group the room to reach the top.
   */
  const scrollToMaster = useCallback(
    (behavior: ScrollBehavior): Point | null => {
      const stage = stageRef.current;
      const nav = railNav();
      const item = masterItem();
      const row = item?.querySelector<HTMLElement>('button');
      if (!stage || !nav || !item || !row) return null;
      const navTop = layoutOffset(nav, stage).y;
      const rowOffset = layoutOffset(row, stage);
      const maxScroll = Math.max(0, nav.scrollHeight - nav.clientHeight);
      const target = Math.min(maxScroll, Math.max(0, rowOffset.y - navTop - NAV_TOP_GAP));
      nav.scrollTo({ top: target, behavior });
      return { x: 0, y: rowOffset.y - target + row.offsetHeight / 2 };
    },
    [railNav, masterItem]
  );

  const play = useCallback(() => {
    clearTimers();
    setCursor(null);
    setCamera(null);
    setPhase('rest');
    railNav()?.scrollTo({ top: 0 });
    setPlayCount((count) => count + 1);
    if (reduced) {
      // One static frame: the menu open on Pengaturan, with Master open in the full film.
      setPhase('pengaturan');
      return;
    }
    for (const [at, next] of TIMELINES[variant]) {
      schedule(at, () => {
        setPhase(next);
        if (next === 'zoom') {
          const focus = scrollToMaster('smooth');
          setCamera(focus ? { focus } : null);
        } else if (next === 'account') {
          // Pull back to the whole frame: the rail (Master open) and the top bar in one shot.
          setCamera(null);
        } else if (next === 'done') {
          setCamera(null);
          railNav()?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }
  }, [clearTimers, railNav, reduced, schedule, scrollToMaster, variant]);

  // Master has just opened: its sub-list may let the row climb further, so
  // aim again from the layout as it is now.
  useEffect(() => {
    if (reduced || phase !== 'open') return;
    const id = window.requestAnimationFrame(() => {
      const focus = scrollToMaster('smooth');
      if (focus) setCamera({ focus });
    });
    return () => window.cancelAnimationFrame(id);
  }, [phase, reduced, scrollToMaster]);

  // Reduced motion renders Master open in one commit; scroll the rail to it
  // after that, on the first play and on every replay.
  useEffect(() => {
    if (reduced && phase === 'pengaturan' && fullFilm) scrollToMaster('auto');
  }, [reduced, phase, fullFilm, scrollToMaster, playCount]);

  // Cursor follows what each phase is about, re-measured while the rail's
  // scroll, the group opening and the camera move are still settling.
  useEffect(() => {
    if (reduced) return;
    const stage = stageRef.current;
    const target = (): Element | null | undefined => {
      if (phase === 'zoom' || phase === 'open') return masterItem()?.querySelector('button');
      if (phase === 'statement' || phase === 'consignee') return masterItem()?.querySelector('[aria-current="page"]');
      if (phase === 'account') return stage?.querySelector(accountSelector);
      if (phase === 'pengaturan') return stage?.querySelector(pengaturanSelector);
      return null;
    };
    if (!target()) {
      setCursor(null);
      return;
    }
    const ids: number[] = [];
    // The camera transition is 900 ms; the last sample lands just after it.
    for (const delay of [60, 260, 520, 800, 950]) {
      ids.push(
        window.setTimeout(() => {
          const point = measure(target());
          if (point) setCursor(point);
        }, delay)
      );
    }
    return () => {
      for (const id of ids) window.clearTimeout(id);
    };
  }, [phase, measure, reduced, masterItem]);

  useEffect(() => {
    const id = window.setTimeout(play, 350);
    return () => {
      window.clearTimeout(id);
      clearTimers();
    };
  }, [play, clearTimers]);

  // Master stays open, Data Consignee active, while the camera moves on to the menu.
  const masterOpen = fullFilm && (OPEN_PHASES.includes(phase) || MENU_PHASES.includes(phase));
  const activeLink = !fullFilm
    ? ''
    : phase === 'statement'
      ? STATEMENT_LINK
      : phase === 'consignee' || MENU_PHASES.includes(phase)
        ? CONSIGNEE_LINK
        : '';
  const menuOpen = phase === 'pengaturan';
  const pengaturanHighlighted = phase === 'pengaturan';

  // Scale about the focus point with the origin at the stage's top-left, so
  // every shot is one `transform` and the move between shots animates.
  const cameraOn = !reduced && camera && MASTER_PHASES.includes(phase);
  const cameraTransform = cameraOn
    ? `translate(${(1 - ZOOM) * camera.focus.x}px, ${(1 - ZOOM) * camera.focus.y}px) scale(${ZOOM})`
    : 'translate(0px, 0px) scale(1)';

  const labelOf = (entry: { label: string; i18nKey?: string }) => (entry.i18nKey ? tApp(entry.i18nKey) : entry.label);
  const badgeOf = (badge?: string) => (badge ? (BADGE_KEYS[badge] ? tApp(BADGE_KEYS[badge]) : badge) : undefined);
  const displayName = accountName || userEmail;
  const initial = (displayName || '?').charAt(0).toUpperCase();
  const noop = () => {};

  const gatewayLogo = isGatewayUser ? (
    <img
      src="/image/jpg/gatewaycontainerline_logo.jpeg"
      alt="Gateway"
      className="h-8 w-auto max-w-25 rounded-control"
    />
  ) : undefined;

  return (
    <div>
      <div
        ref={frameRef}
        data-film-phase={phase}
        data-film-variant={variant}
        className="relative overflow-hidden rounded-panel border border-line bg-canvas"
        style={{ height: FRAME_HEIGHT }}
      >
        <div
          ref={stageRef}
          inert
          aria-hidden="true"
          className="absolute inset-0 flex"
          style={{
            transform: cameraTransform,
            transformOrigin: '0px 0px',
            transition: reduced ? 'none' : 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
          }}
        >
          <div className="relative h-full flex-none" style={{ width: Math.round(RAIL_WIDTH * textScale) }}>
            <SidebarRail
              collapsed={false}
              toggleCollapsed={noop}
              mobileOpen={false}
              setMobileOpen={noop}
              isDesktop
              logoId="first-login-film"
              brandExtra={gatewayLogo}
            >
              <SidebarNavList label={tApp('lumen.sidebar.navLabel')}>
                {items.map((item) => {
                  const label = labelOf(item);
                  if (item.locked) {
                    return (
                      <SidebarNavItem
                        key={item.label}
                        label={label}
                        icon={item.icon}
                        collapsed={false}
                        locked
                        lockedLabel={tApp('lumen.sidebar.locked')}
                      />
                    );
                  }
                  if (item.subLinks) {
                    const isMaster = item.label === MASTER_LABEL;
                    return (
                      <SidebarNavGroup
                        key={item.label}
                        label={label}
                        icon={item.icon}
                        collapsed={false}
                        active={isMaster && activeLink !== ''}
                        badge={badgeOf(item.badge)}
                        open={isMaster && masterOpen}
                        onOpenChange={noop}
                        items={item.subLinks.map((sub) => ({
                          to: sub.link,
                          label: labelOf(sub),
                          icon: sub.icon,
                          active: isMaster && sub.link === activeLink,
                        }))}
                      />
                    );
                  }
                  return (
                    <SidebarNavItem
                      key={item.label}
                      label={label}
                      icon={item.icon}
                      collapsed={false}
                      to={item.link}
                      badge={badgeOf(item.badge)}
                    />
                  );
                })}
              </SidebarNavList>
            </SidebarRail>
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* The top bar as the app shows it, reduced to what the film points at. */}
            <div className="relative flex h-12 flex-none items-center justify-between gap-2 border-line border-b bg-canvas px-3">
              <div className="flex h-8 min-w-0 max-w-56 flex-1 items-center gap-2 rounded-control border border-line bg-surface px-2.5 text-fg-muted">
                <SearchIcon className="size-3.5 flex-none" />
                <span className="h-1.5 w-1/2 rounded-full bg-line-strong" />
              </div>
              <div className="flex flex-none items-center gap-1">
                <span className="flex size-8 items-center justify-center text-fg-2">
                  <BellIcon className="size-4" />
                </span>
                <span
                  data-film-account=""
                  className={cn('flex h-8 items-center gap-1.5 rounded-control px-1 text-fg', menuOpen && 'bg-tint')}
                >
                  <span
                    className={cn(
                      'flex size-6.5 flex-none items-center justify-center rounded-full bg-surface-2 font-bold text-fg text-xs',
                      isImpersonating && 'bg-warn/15 text-warn-ink ring-1 ring-warn/50'
                    )}
                  >
                    {initial}
                  </span>
                  <ChevronDownIcon className="size-4 text-fg-muted" />
                </span>
              </div>

              {/* The account menu (AccountMenu.tsx), drawn in place rather than portalled: a real
                  dropdown would open on <body> under the wizard. */}
              {menuOpen && (
                <div className="absolute top-[calc(100%+4px)] right-2 z-10 w-64 rounded-card border border-line-strong bg-surface p-1 text-fg shadow-overlay">
                  <div className="flex flex-col gap-0.5 px-2 py-2">
                    {accountName && <span className="truncate font-semibold text-fg text-sm">{accountName}</span>}
                    <span className="truncate text-fg-muted text-xs">{userEmail}</span>
                  </div>
                  <div className="-mx-1 my-1 h-px bg-line" />
                  {canImpersonate && (
                    <div className="flex items-center gap-2 rounded-tag px-2 py-1.5 text-sm">
                      <DramaIcon className="size-4 text-fg-muted" />
                      {tApp(isImpersonating ? 'sidebar.impersonating' : 'sidebar.impersonate')}
                    </div>
                  )}
                  <div
                    data-film-pengaturan=""
                    className={cn(
                      'flex items-center gap-2 rounded-tag px-2 py-1.5 text-sm',
                      pengaturanHighlighted && 'bg-surface-2 text-fg'
                    )}
                  >
                    <SettingsIcon className="size-4 text-fg-muted" />
                    {tApp('menu.pengaturan')}
                  </div>
                  <div className="-mx-1 my-1 h-px bg-line" />
                  <div className="flex items-center gap-2 rounded-tag px-2 py-1.5 text-err text-sm">
                    <LogOutIcon className="size-4" />
                    {tApp('sidebar.logout')}
                  </div>
                </div>
              )}
            </div>

            {/* The page behind, as blank as a freshly opened screen. */}
            <div className="flex-1 p-4.5">
              <div className="h-3 w-2/5 rounded-tag bg-line-strong" />
              {[0.95, 0.7, 0.55, 0.9, 0.7].map((w, index) => (
                <div
                  key={`${index}-${w}`}
                  className="mt-2.5 h-2 rounded-tag bg-line"
                  style={{ width: `${w * 100}%` }}
                />
              ))}
            </div>
          </div>
        </div>

        {cursor && (
          <div
            data-film-cursor
            aria-hidden="true"
            className="pointer-events-none absolute z-20 h-5 w-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
            style={{
              left: cursor.x,
              top: cursor.y,
              transition: 'left 520ms cubic-bezier(0.22, 1, 0.36, 1), top 520ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <svg viewBox="0 0 14 18" width="16" height="20" role="presentation">
              <path
                d="M1 1 L1 14 L4.5 10.8 L7 16.5 L9.5 15.4 L7 9.8 L12 9.6 Z"
                fill="var(--lm-raised)"
                stroke="var(--lm-fg)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={play}>
          <PlayIcon aria-hidden="true" />
          {t('film.replay')}
        </Button>
        <span className="text-13 text-fg-muted">{caption}</span>
      </div>
    </div>
  );
}
